// src/components/roles/forms/CreateRole.tsx
import React, { useEffect, useState } from "react";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiType } from "react-icons/fi";

export type UpsertRole = {
  name: string;
  isSystemRole: boolean;
};

export default function CreateRole({
  onSubmit,
  onCancel,
  defaultValues,
}: {
  onSubmit: (payload: UpsertRole) => void;
  onCancel?: () => void;
  defaultValues?: Partial<UpsertRole>;
}) {
  const [form, setForm] = useState<UpsertRole>({
    name: defaultValues?.name ?? "",
    // Staff roles are assignable by default; untick for section-managed roles.
    isSystemRole: defaultValues?.isSystemRole ?? true,
  });

  // If the modal is reused with different defaults, sync once it opens
  useEffect(() => {
    setForm((p) => ({ ...p, name: defaultValues?.name ?? "" }));
  }, [defaultValues?.name]);

  const change = <K extends keyof UpsertRole>(k: K, v: UpsertRole[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit({ name: form.name.trim(), isSystemRole: form.isSystemRole });
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Role name" icon={<FiType />}>
        <Input
          value={form.name}
          onChange={(e) => change("name", e.target.value)}
          placeholder="e.g., Admin"
          required
        />
      </Field>

      <label className="sm:col-span-2 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
          checked={form.isSystemRole}
          onChange={(e) => change("isSystemRole", e.target.checked)}
        />
        <span>
          <span className="font-medium text-gray-900">System user role</span>
          <span className="mt-0.5 block text-xs text-gray-500">
            Make this role assignable when creating or editing users. Leave
            unchecked for roles managed from their own section (e.g. Customer,
            Affiliate Partner, Supplier).
          </span>
        </span>
      </label>

      <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => onCancel?.()}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          Create Role
        </button>
      </div>
    </form>
  );
}
